import { getWebInstrumentations, initializeFaro } from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { SERVICE_NAME } from "@zapier/utils";

import { isPreview, isProduction } from "../utils/environment";
import { isDatadog } from "../utils/isDatadog";

let faro: ReturnType<typeof initializeFaro> | undefined;

const appName = `${SERVICE_NAME}-ui`;
const shouldTrackToFaro = isProduction || isPreview;
const firstPartyUrls = [/https:\/\/(.*\.)?zapier(-staging)?\.com/];

const otherAgentUrls = [
  /https:\/\/browser-intake-[a-z0-9-]+\.(com|eu)\//,
  /https:\/\/rs\.fullstory\.com\//,
  /https:\/\/o\d+\.ingest\.([a-z]{2}\.)?sentry\.io\//,
];

export function initializeGrafanaFaro() {
  if (globalThis.window === undefined || faro) {
    return;
  }

  const url = process.env.NEXT_PUBLIC_FARO_COLLECTOR_URL;
  if (!url) {
    console.info("Grafana Faro not initialized: missing collector URL.");
    return;
  }

  if (!shouldTrackToFaro) {
    console.info(
      "Grafana Faro not initialized: unsupported deployment environment.",
    );
    return;
  }

  if (isDatadog()) {
    console.info("Grafana Faro not initialized: synthetic user agent.");
    return;
  }

  const sampleRateOverride = Number(
    process.env.NEXT_PUBLIC_FARO_SAMPLE_RATE ?? NaN,
  );

  // Turbopack does not run Faro's bundler plugin. Set the global that
  // faro-core reads so browser errors match the uploaded commit bundle.
  if (process.env.NEXT_PUBLIC_COMMIT_SHA) {
    (globalThis as unknown as Record<string, string>)[
      `__faroBundleId_${appName}`
    ] = process.env.NEXT_PUBLIC_COMMIT_SHA;
  }

  faro = initializeFaro({
    url,
    app: {
      name: appName,
      environment: process.env.NEXT_PUBLIC_VERCEL_ENV,
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
    },
    // Faro uses a 0-1 fraction. Datadog uses a 0-100 percentage.
    sessionTracking: {
      samplingRate:
        Number.isFinite(sampleRateOverride) &&
        sampleRateOverride >= 0 &&
        sampleRateOverride <= 1
          ? sampleRateOverride
          : 0.5,
    },
    ignoreUrls: otherAgentUrls,
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        omitTraceContextForUnsampledSessions: true,
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: firstPartyUrls,
        },
      }),
    ],
  });
}

export function setGrafanaFaroUser(user?: { id: string; accountId: string }) {
  if (!user) {
    faro?.api.resetUser();
    return;
  }

  faro?.api.setUser({
    id: user.id,
    attributes: { account_id: user.accountId },
  });
}
